import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const actorType = localStorage.getItem('actorType') || 'staff';
      setUser({
        email: localStorage.getItem('userEmail'),
        role: localStorage.getItem('userRole'),
        userId: localStorage.getItem('userId'),
        actorType,
        sellerName: localStorage.getItem('sellerName') || null,
        token: token // Add token to user object
      });
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3012/api';
      const response = await axios.post(`${apiUrl}/employees/login`, {
        email,
        password
      });

      const { token, employee } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userEmail', email);
      localStorage.setItem('userRole', employee.role);
      localStorage.setItem('userId', employee.id);
      localStorage.setItem('actorType', 'staff');
      localStorage.removeItem('sellerName');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({
        email: employee.email,
        role: employee.role,
        userId: employee.id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        actorType: 'staff',
        token: token // Add token to user object
      });
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      return { success: false, error: err.response?.data?.message || 'Login failed' };
    }
  };

  // Vendor (dealer / dryer manufacturer) sign-in. Vendors live in their own
  // token world — role 'vendor' matches nothing on the staff role ladder, so
  // every staff page and nav item hides itself, and the server rejects their
  // token on every staff endpoint anyway.
  const loginVendor = async (email, password) => {
    try {
      setError(null);
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3012/api';
      const response = await axios.post(`${apiUrl}/vendor/login`, {
        email,
        password
      });

      const { token, vendor } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userEmail', vendor.email);
      localStorage.setItem('userRole', 'vendor');
      localStorage.setItem('userId', vendor.id);
      localStorage.setItem('actorType', 'vendor');
      localStorage.setItem('sellerName', vendor.seller?.name || '');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({
        email: vendor.email,
        role: 'vendor',
        userId: vendor.id,
        firstName: vendor.firstName,
        lastName: vendor.lastName,
        actorType: 'vendor',
        sellerName: vendor.seller?.name || null,
        token: token
      });
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      return { success: false, error: err.response?.data?.message || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('actorType');
    localStorage.removeItem('sellerName');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    login,
    loginVendor,
    logout,
    loading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
